import { ButtonBase, ButtonBaseProps, useMediaQuery } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import { getDesignTokens } from '~/client/components/base/core';
import { spc } from '~/client/components/base/spacer';
import Text from '~/client/components/base/text';

interface Props extends ButtonBaseProps {
  className?: string;
  icon?: OverridableComponent<SvgIconTypeMap>;
}

const QuickActionButtonImpl = React.forwardRef<HTMLButtonElement, Props>(
  ({ className, icon, children, ...props }, ref) => {
    const Icon = icon;

    return (
      <div className={className}>
        <ButtonBase
          ref={ref}
          className="v2-quick-action-button"
          sx={{ p: spc.container_padding_m }}
          {...props}
        >
          {Icon && <Icon className="v2-icon" />}
          <Text className="v2-text" sz="body16" noWrap align="left">
            {children}
          </Text>
        </ButtonBase>
      </div>
    );
  }
);

/**
 * Quick Action Button
 *
 * A Button component designed to be placed within QuickActionDrawer
 *
 * It inherits all properties of a Button, and can optionally display an icon
 */
export const QuickActionButton = muiStyled(QuickActionButtonImpl)(({
  theme,
}) => {
  const t = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette ?? 'current'
  );
  const { disabledOpacity, activatedOpacity } = theme.palette.action;
  return css`
    .v2-quick-action-button {
      /* Button to take up width of container */
      width: 100%;

      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;

      height: 86px;
      background: ${t.surface2};
      border: 1px solid ${t.borderDefault};
      border-radius: ${t.radiusSmall}px;

      color: ${t.textPrimary};

      :disabled {
        opacity: ${disabledOpacity};
      }
      :hover {
        border-color: ${t.accentBorder};
        color: ${t.accent};
        .MuiTypography-root {
          color: ${t.accent};
        }
      }
      :active {
        opacity: ${activatedOpacity};
      }
    }

    .v2-icon {
      font-size: 16px;
      color: ${t.iconInteractive};
    }

    .v2-text {
      max-width: 100%;
      .MuiTypography-root {
        color: ${t.textPrimary};
      }
    }
  `;
});
